<!--
     <DateInput
       :props="my_attrs_dictionary"
     />

     Public propertys:
       value → Gives the field's serializable value

     Accepted props:
     - value
     - class
     - label
     - format
-->

<template>
  <div>
    <v-menu
      :value="menu"
      offset-y
      max-width="300"
      :disabled="!!props.readonly"
      @input="menu_opened"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          :name="props.id"
          :value="value"
          :label="props.label"
          :class="[
            props.class || props.readonly ? 'qmkey-form-input-readonly': 'qmkey-form-input',
            { required: props.required }
          ]"
          :readonly="props.readonly"
          v-bind="attrs"
          v-on="on"
          :rules="(rules || []).concat(props.rules || [])"
          outlined
          dense
          background-color="#f2f2f2"
          :clearable="!props.required && !props.readonly"
          @click.prevent
          @click:clear="local_value=null"
          :disabled="!!props.disabled || !!props.readonly"
          type="date"
          @input="manual_date"
          @change="manual_date"
          :error-messages="props.error_message"
          @click.stop="click_outside"
          @click="open_menu"
          @keydown="keyboard_esc_event"
        >
          <v-icon 
            slot="append"
            color="primary"
            v-bind="attrs"
            v-on="on"
            v-if="datePicker || (props.disabled || props.readonly)"
            :disabled="props.disabled || props.readonly"
            @click="menu = true"
          >
            {{ false || "mdi-calendar" }}
          </v-icon>
          <div v-else slot="append"></div> <!-- Hide icon -->
        </v-text-field>
      </template>
      <v-date-picker
        id="picker"
        :value="value"
        no-title
        @change="(val) => {manual_date(val);  close_menu()}"
        :locale="$vuetify.lang.current"
        first-day-of-week="1"
        @keydown="keyboard_esc_event"
      ></v-date-picker>
    </v-menu>
  </div>
</template>


<script>

export default {
  name: 'DateInput',

  props: ['props'],

  data: () => ({
    local_value: null,
    menu: false,
    valid: true,
    datePicker: true,
    error_msg: "",
    error: false,
    must_be_opened: true,
  }),

  mounted() {
    if (this.props.value) {
      /* FIXME: Backend send plain dates into 2 formats with or without time
        "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM:SS".
        Ensure initial plain date format
      */
      this.local_value = this.props.value.split('T')[0];
    }

    // TODO: erase this, remove OUR date icon on Firefox
    this.datePicker = !navigator.userAgent.includes("Firefox")
  },

  computed: {
    value() {
      return (this.props.value || '').split('T')[0] || null;
    },
    rules() {
      return [
        (value) => !value || this.correct_date(value) || this.$vuetify.lang.translator('invalid_date'),
        (value) => !!value || !this.props.required || this.$vuetify.lang.translator('required_field'),
      ]
    },
  },
  
  methods: {

    menu_opened(open) {
      this.menu = open;
      if (this.must_be_opened) {
        this.$nextTick(()=>{
          this.open_menu();
        })
      }
    },
    open_menu() {
      this.must_be_opened=true;
      this.menu = true;

      //Add all event listeners, for click outside and esc keyboard event
      document.addEventListener("click",this.click_outside);
      document.body.addEventListener("keydown",this.keyboard_esc_event);
      document.body.addEventListener("click",this.click_outside);
    },

    close_menu() {
      document.activeElement.blur()
      this.must_be_opened=false;
      this.menu = false;

      //Remove all event listeners, for click outside and esc keyboard event
      document.removeEventListener("click",this.click_outside); 
      document.body.removeEventListener("keydown",this.keyboard_esc_event);
      document.body.removeEventListener("click",this.click_outside);

    },
    not_valid(a) {
      this.error_msg = this.$vuetify.lang.translator('invalid_date');
      this.error = a;
    },
    validate() {
      // TODO: Show error message
      return !this.props.required || !!this.value;
    },
    error_state(ok) {
      this.valid = ok;
    },
    update(value) {
      this.local_value = value ? value : null;
      this.$emit('update',this.local_value)
    },

    update_date(date) {
      if (date) {
        this.local_value = date;
      }
      this.menu = false;
    },

    correct_date(str_date) {
      const [year,month,day] = (str_date || '').split('-').map(it => parseInt(it));
      return this.correct_day(day) && this.correct_month(day,month) && this.correct_year(day,month,year);
    },

    correct_day(day) {
      return day > 0 && day <= 31;
    },

    correct_month(day,month) {
      let ok = month > 0 && month <= 12;
      if (ok) {
        // Feb is special
        if (month != 2) {
          ok = day > 0 && day <= 31;
        } else {
          ok = day > 0 && day < 30;
        }
      }
      return ok;
    },

    correct_year(day,month,year) {
      // Year is 4 digits, maybe use closer range >=1900 && <=2100
      let ok = year >= 1900 && year <= 2100;
      if (ok) {
        const current_date = new Date(year,month-1,day);
        ok = current_date.getDate() == day && current_date.getMonth()+1 == month && current_date.getFullYear() == year;
      }
      return ok;
    },

    manual_date(input) {
      if(input) {
        this.local_value = input;
      } else {
        this.local_value = null;
      }
      if (this.correct_date(input) || !this.props.required) {
        this.error = false;
        this.update(this.local_value);
      } else if (!input && this.props.required) {
        this.error = false;
        this.update(this.local_value);
      }
     },

    //TODO: Copy / paste

    keyboard_esc_event(event) {
      //Close menu with esc keyboard
      if (this.menu && (event.key == 'Escape' || event.key == 'Enter')) {
        this.close_menu();
        event.stopPropagation();
        event.preventDefault();
      }
    },

    click_outside(event) {
      if (event.target.name != this.props.id && !event.target.closest("#picker")) {
        event.stopPropagation();
        event.preventDefault();
        this.close_menu();
      }
    },
  },
  watch: {
  },
}
</script>
<style lang="scss">
@import '@/sass/modalform.scss';
input[type="date"]::-webkit-calendar-picker-indicator  {
  display: none;
}
</style>
